import React from 'react';
import { useTranslation } from 'react-i18next';
import { MoreHorizontal } from 'lucide-react';
import { ProjectTypeIcon } from './ProjectTypeIcon';
import { Touchable } from './touchable';

export interface DisplaySession {
  id: string;
  title: string;
  progress: number;
  currentRow: number;
  totalRows: number;
  updatedAt: Date;
  archived: boolean;
  metadata?: Record<string, string>;
  /** Resolved pattern color (hex), used to tint the project-type icon. */
  color: string;
}

interface SessionCardProps {
  session: DisplaySession;
  onSelect: (sessionId: string) => void;
  onManage: (session: DisplaySession) => void;
}

export const SessionCard: React.FC<SessionCardProps> = ({ session, onSelect, onManage }) => {
  const { t } = useTranslation('session');
  return (
    <div
      role="button"
      tabIndex={0}
      aria-label={session.title}
      onClick={() => onSelect(session.id)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect(session.id);
        }
      }}
      className="relative w-full text-left bg-card p-5 rounded-3xl shadow-sm border border-line active:scale-[0.98] transition-all group cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary"
    >
      <Touchable
        haptic="softTap"
        onClick={(e) => {
          e.stopPropagation();
          onManage(session);
        }}
        title={t('card.manage', 'Manage session')}
        aria-label={t('card.manage', 'Manage session')}
        className="absolute top-3 right-3 w-9 h-9 rounded-full flex items-center justify-center text-ink-faint hover:bg-soft hover:text-ink transition-colors"
      >
        <MoreHorizontal className="w-5 h-5" />
      </Touchable>

      <div className="flex items-start gap-3 mb-3 pr-10">
        <ProjectTypeIcon
          metadata={session.metadata}
          color={session.color}
          size={32}
          className="text-primary-strong shrink-0 mt-0.5"
        />
        <div className="flex-grow flex justify-between items-start gap-2">
          <h3 className="font-bold text-ink group-active:text-primary transition-colors">
            {session.title}
          </h3>
          <span className="text-[10px] font-bold text-ink-faint uppercase tracking-widest whitespace-nowrap">
            {t('card.rowOf', 'Row {{row}} / {{total}}', {
              row: session.currentRow + 1,
              total: session.totalRows,
            })}
          </span>
        </div>
      </div>

      <div className="w-full bg-soft h-2.5 rounded-full overflow-hidden">
        <div
          className={`${session.archived ? 'bg-primary-strong/40' : 'bg-primary'} h-full rounded-full transition-all`}
          style={{ width: `${session.progress}%` }}
        />
      </div>

      {session.archived && (
        <span className="inline-block mt-3 text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full bg-soft text-ink-faint">
          {t('card.archived', 'Archived')}
        </span>
      )}
    </div>
  );
};

interface SessionCardGridProps {
  columns: 'single' | 'responsive';
  children: React.ReactNode;
}

export const SessionCardGrid: React.FC<SessionCardGridProps> = ({ columns, children }) => {
  const cls =
    columns === 'single'
      ? 'grid grid-cols-1 gap-4'
      : 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4';
  return <div className={cls}>{children}</div>;
};
